import { type ButtonHTMLAttributes, type HTMLAttributes, type LiHTMLAttributes, type ReactNode } from 'react';
import { X } from '../../../icons';
import './Chip.css';

/**
 * Chip — **담긴 것 하나**를 나타내는 알약 (디자인 정본 D14, 2026-09-07 신설).
 *
 * ★★★ **왜 신설했나 — 여섯 벌이 따로 살았다**(2026-09-07 실측):
 *    결재 사람 칩 · 게시판 필터 칩 · 메일 수신자 칩 · 메일 바깥주소 칩 · 워크스페이스 칩,
 *    그리고 **정본 `MultiValueInput`(B2-2) 자신의 `.wc-mvi__chip`**.
 *    「정본에 없으면 다음 사람은 또 손으로 짠다」가 **정본 안에서** 일어난 자리다.
 *    바탕이 셋 · 테두리 유무 둘 · 모서리 셋 · 굵기 셋이었다.
 *
 * ★★ **두 갈래인데 모양은 하나다.**
 *    · `Chip` — **담김 칩**. 골라 «담은» 것이고 `<li>` 다(목록의 낱개). `onRemove` 로 × 가 붙는다.
 *    · `FilterChip` — **고르기 칩**. 눌러서 거르는 것이고 `<button aria-pressed>` 다.
 *    ★ 둘을 한 컴포넌트에 `as` 로 합치지 않는다 — **읽어 주는 뜻이 다르다**(항목 vs 눌림 단추).
 *
 * ★ **B2-2 `MultiValueInput` 과 가르는 자리** — B2-2 는 «칸»이고 이것은 «그 안에 담기는 낱개»다.
 *
 * ★★ **`inline` — 입력 칸 «안»에 서는 칩** (2026-09-07).
 *    메일 받는사람·`MultiValueInput` 은 칩과 `<input>` 이 **한 줄에 섞여** 흐른다.
 *    `<ul>` 은 `<li>` 말고 다른 것을 품을 수 없어서 그 자리에는 `Chips` 를 쓸 수 없다 —
 *    `inline` 이면 `<span role="listitem">` 으로 그린다(바깥 상자가 `role="list"` 를 진다).
 *    ★ **읽어 주는 뜻은 같다** — 바뀌는 것은 태그뿐이다.
 */
export type ChipVariant = 'neutral' | 'primary' | 'quiet';
export type ChipSize = 'sm' | 'md';

interface ChipBase {
  variant?: ChipVariant;
  size?: ChipSize;
  /** 앞 슬롯 — 결재선의 순서 번호 · 아바타. */
  lead?: ReactNode;
  children: ReactNode;
  className?: string;
}

export interface ChipProps extends ChipBase, Omit<LiHTMLAttributes<HTMLLIElement>, 'children' | 'className'> {
  /**
   * 입력 칸 «안»에 설 때 켠다 — `<span role="listitem">` 으로 그린다.
   * ★ 그 자리의 바깥 상자가 `role="list"` 를 지고 있어야 한다(칩만 담는 `<ul>` 을 못 쓰기 때문).
   */
  inline?: boolean;
  /**
   * 주면 뒤에 × 가 붙는다.
   * ★ `removeLabel` 이 **필수**다 — 없으면 스크린리더가 「단추」로만 읽어, 열 개가 서면
   *   무엇을 지우는지 알 수 없다.
   */
  onRemove?: () => void;
  removeLabel?: string;
  removeDisabled?: boolean;
}

function chipClass(variant: ChipVariant, size: ChipSize, extra?: string): string {
  return [
    'wc-chip',
    `wc-chip--${size}`,
    variant !== 'neutral' ? `wc-chip--${variant}` : '',
    extra ?? '',
  ].filter(Boolean).join(' ');
}

export function Chip({
  variant = 'neutral', size = 'sm', lead, children, className, inline = false,
  onRemove, removeLabel, removeDisabled = false, ...rest
}: ChipProps) {
  const Tag = (inline ? 'span' : 'li') as 'li';
  const tagProps = inline
    ? ({ role: 'listitem', ...rest } as HTMLAttributes<HTMLElement>)
    : rest;
  return (
    <Tag className={chipClass(variant, size, className)} {...tagProps}>
      {lead !== undefined && <span className="wc-chip__n">{lead}</span>}
      {/* 긴 이름은 줄이지 않고 «자른다» — 칩이 줄을 밀어내면 그 줄의 다른 칩이 안 보인다. */}
      <span className="wc-chip__tx">{children}</span>
      {onRemove && (
        <button
          type="button"
          className="wc-chip__x"
          aria-label={removeLabel}
          disabled={removeDisabled}
          onClick={onRemove}
        >
          <X size={12} aria-hidden />
        </button>
      )}
    </Tag>
  );
}

export interface FilterChipProps
  extends ChipBase, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> {
  /** 켜져 있는가. `aria-pressed` 로 나가고, 켜지면 tint 가 깔린다. */
  pressed: boolean;
}

export function FilterChip({
  variant = 'neutral', size = 'md', lead, children, className, pressed, ...rest
}: FilterChipProps) {
  return (
    <button
      type="button"
      className={chipClass(variant, size, className)}
      aria-pressed={pressed}
      {...rest}
    >
      {lead !== undefined && <span className="wc-chip__n">{lead}</span>}
      <span className="wc-chip__tx">{children}</span>
    </button>
  );
}

/** 칩을 담는 줄. `<ul>` 이라 읽어 주는 프로그램이 「목록, 항목 3개」로 센다. */
export function Chips({ children, className }: { children: ReactNode; className?: string }) {
  return <ul className={['wc-chips', className ?? ''].filter(Boolean).join(' ')}>{children}</ul>;
}
